import { useMutation, useQueryClient } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface BookMakeupParams {
  studentId: string;
  sourceEnrollmentId: string;
  sourceAttendanceDate: string;
  targetEnrollmentId: string;
  targetDate: string;
  notes?: string;
  sendConfirmation?: boolean;
  /** Staff only — book even when the class occurrence is at capacity. */
  allowOverCapacity?: boolean;
}

export interface MakeupBooking {
  _id: string;
  studioId: string;
  studentId: string | { _id: string; firstName?: string; lastName?: string; name?: string };
  parentId: string;
  sessionId: string;
  sourceEnrollmentId: string | { _id: string; title?: string };
  sourceAttendanceDate: string;
  targetEnrollmentId: string | { _id: string; title?: string; startTime?: string; endTime?: string };
  targetDate: string;
  status: 'requested' | 'scheduled' | 'completed' | 'no_show' | 'cancelled' | 'expired';
  bookingMode: 'staff_direct' | 'parent_request';
  notes?: string;
  completedAt?: string;
  familyCreditConsumed: boolean;
  studentAllotmentConsumed: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const bookMakeupAPI = async (params: BookMakeupParams) => {
  const response = await baseAPI.post<MakeupBooking>('/makeup', params);
  return response.data;
};

export const useBookMakeupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bookMakeupAPI,
    onSuccess: (booking) => {
      void queryClient.invalidateQueries({ queryKey: [KEYS.MAKEUP_BALANCES] });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.MAKEUP_BY_CLASS_DATE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.MAKEUP_BY_SOURCE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.MAKEUP_TARGETS],
      });
      const studentId =
        typeof booking.studentId === 'string'
          ? booking.studentId
          : booking.studentId._id;
      void queryClient.invalidateQueries({
        queryKey: [KEYS.MAKEUP_BY_STUDENT, studentId],
      });
    },
  });
};
